import type { GarageProperty } from "@App/apis/graphql/perimeterSearch/responses/garageProperty";
import type { PresetFilterName } from "@App/types/presetFilters";

declare global {
  var asset_url: string;
  var organization: Organization;
  var order: {
    place: Place;
    service: Service;
  };
  var graphql_url: string;
  var sentry_release: string;
  var csrf_url: string;

  var rest_url: string;
}

export interface Service {
  id: number | string;
  uuid: number | string;
  name: string;
  slug: string;
  garage_prefix: string;
  show_checkpoint: boolean;
  requires_additional_information: boolean;
  display_reviews: boolean;
  pivot: {
    subtitle?: string;
    icon?: string;
    display_name?: string;
    display_search_headline?: string;
    display_image_placeholder?: string;
    sort_order?: number;
    faqs?: FAQ[];
    external_url?: string;
    reviews_enabled?: boolean;
  };
}

export interface FAQ {
  headline: string;
  content: string;
}

export interface Garage {
  id: number | string;
  uuid: number | string;
  slug: string;
  name: string;
  distance: number;
  travel_time?: number;
  lat: number;
  lon: number;
  rating: number;
  review_count: number;
  opening_hours?: OpeningHours[];
  available_days?: AvailableDay[];
  cover_image?: string;
  premium_services?: PremiumService[];
  reviews?: ReviewOverview;
  garageType?: GarageType;
  description?: string;
  street?: string;
  postcode?: string;
  city?: string;
  phone?: string;
  formatted_distance?: string;
  nationwide_service?: boolean;
  days?: string[];
  earliestSlot?: Slot;
  slots?: Slot[];
  images?: Image[];
  checkpoint?: Checkpoint;
  authorizedBrands?: Brand[];
  pivot: {
    bookable: boolean;
    preferred_partner?: boolean;
  };
  liveBookable?: boolean;
  drivingLicenseCheckpoint?: {
    name: string;
  };
  billingGroups?: BillingGroup[];
  garageProperties?: GarageProperty[];
  garageChain?: GarageChain;
}

export interface BillingGroup {
  id: string;
  name: string;
  display_name: string;
}

export interface ReviewOverview {
  distribution?: {
    five: number;
    four: number;
    three: number;
    two: number;
    one: number;
  };
  data?: Review[];
}

export interface GarageType {
  id: number | string;
  name: string;
  display_name_singular: string;
}

export interface Checkpoint {
  id: number | string;
  name: string;
  logo: string;
}

export interface Brand {
  id: number | string;
  name: string;
  slug?: string;
  logo: string;
  important?: boolean;
  generations?: Generation[];
  pivot?: BrandGaragePivot;
}

export interface BrandGaragePivot {
  type: string;
}

export interface Generation {
  id: number | string;
  name: string;
  slug?: string;
  built_from?: Date;
  motorizations?: Motorization[];
}

export interface Motorization {
  id: number | string;
  name: string;
  slug?: string;
  built_from?: Date | string;
  built_to?: Date | string;
  kw: number;
  ps?: number;
  displacement: number;
  fuelType?: FuelType;
  bodies?: Body[];
  gearboxes?: Gearbox[];
  equipments?: Equipment[];
}

export interface KeyNumber {
  id: number | string;
  name: string;
  brand?: Brand;
  generation?: Generation;
  motorization?: Motorization;
}

export interface Key {
  hsn: string;
  tsn: string;
}

export interface FuelType {
  id: number;
  name: string;
}

export interface Body {
  id: number;
  name: string;
}

export interface Gearbox {
  id: number;
  name: string;
  formatted_name: string;
}

export interface Equipment {
  id: number;
  name: string;
}

export interface OpeningHours {
  day: number;
  open: string;
}

export interface Image {
  id: number;
  path: string;
}

export interface Review {
  id: number;
  author: string;
  comment: string;
  rating: number;
  cleanliness: number;
  quality: number;
  punctuality: number;
  competence: number;
  kindness: number;
  published_at: Date;
  is_verified: boolean;
  vendor_name: string;
}

export interface AvailableDay {
  id: string | number;
  available_at: string;
}

export interface Slot {
  id?: string | number;
  starts_at: string;
  ends_at?: string;
  blocked_at?: string;
  calendar_id?: number;
  session_id?: string;
}

export interface PremiumService {
  id: number;
  name: string;
  paid: boolean;
  filterable?: boolean;
  show_in_list?: boolean;
  type?: string;
  service_type?: string;
}

export type ID = number | string;

export interface Organization {
  id: number;
  uuid: number | string;
  name?: string;

  slug?: string;
  garages?: {
    network: Network;
    data: Garage[];
    paginatorInfo: PaginatorInfo;
  };
  services?: Service[];
  premium_services?: PremiumService[];
  network?: Network | null;
  contractDetails?: ContractDetails;
  contractDetailsPickUp?: ContractDetails;
  settings?: {
    language: string;
    embedded?: boolean;
    navbar?: {
      partnerLogo: {
        url: string;
      };
    };
    hero: {
      background_image_url: string;
      link?: string;
      service_links?: {
        id: string;
        url: string;
      }[];
    };
    welcome_modal: {
      display: boolean;
    };
    garage_list: {
      display_prices: boolean;
      display_premium_services: boolean;
      filters: string[];
      default_sorting?: string;
      default_radius_km?: number;
      sorting: string[];
    };
    contactDetails: {
      image: string;
    };
  };
}

export interface User {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  numberPlate: string;
  message: string;
  acceptedPrivacy: boolean;
}

export enum ResultType {
  PLACE = "place",
  LOCALITY = "locality",
  STREET = "street",
  HOUSE_NUMBER = "houseNumber",
  ADMINISTRATIVE_AREA = "administrativeArea",
  ADDRESS_BLOCK = "addressBlock",
  INTERSECTION = "intersection",
  POSTALCODE_POINT = "postalCodePoint",
  CHAIN_QUERY = "chainQuery",
  CATEGORY_QUERY = "categoryQuery",
}

export interface Place {
  id: string;
  lat?: number;
  lon?: number;
  name: string;
  resultType: ResultType;
  language: string;
  /** ISO 3166-1 alpha-3 as returned by HERE ("DEU", "AUT"). Normalize with `fromHereCountryCode`. */
  countryCode?: string;
  address?: Address;
}

export interface Address {
  city?: string;
  county?: string;
  countryName?: string;
  postalCode?: string;
  countyCode?: string;
  state?: string;
  street?: string;
  houseNumber?: string;
}

export interface SortBy {
  id: string;
  name: string;
}

export interface Inquiry {
  id: string;
  uuid: number | string;
}

export interface PaginatorInfo {
  count: number;
  currentPage: number;
  firstItem: number | null;
  hasMorePages: boolean;
  lastItem: number | null;
  lastPage: number;
  perPage: number;
  total: number;
}

export interface DateFilter {
  from: Date;
  to: Date;
}

export interface PhoneInput {
  country: string;
  number: string | null;
}

export interface ContractDetails {
  carId: string | null;
  agreementNumber: string | null;
  name: string | null;
  street: string | null;
  email?: string;
  country?: string;
  postcode: string | null;
  city: string | null;
  phone: string | null;
  earliestDeliveryDate?: string | null;
  contractEndDate?: Date | null;
  returnData?: {
    authorizedGarageIds?: string[] | null;
  };
}

export interface DeliveryDates {
  date1: Date | null;
  date2: Date | null;
  date3: Date | null;
}

export interface GarageChain {
  id: number;
}

export interface PresetFilter {
  name: PresetFilterName;
}

export interface RentalCar {
  category: string;
  equipment: string[];
}

/**
 * A network represents a collection of garages, services, and related entities grouped under a common organization.
 * Usually we have one network per "service" where all garages for that service and organization are grouped under that network. This is how the FE funnels the users.
 */
export interface Network {
  id: string;
  uuid: string;
  name: string;
  organization?: Organization | null;
  garages?: Garage[] | null;
  services?: Service[] | null;
  premiumServices?: Service[] | null;
  garageChains?: GarageChain[] | null;
  billingGroups?: BillingGroup[] | null;
}
